import { useCallback, useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import { TimelineScale } from '../../../types/interfaces';
import { CalendarViewMode } from '../types';

export interface UseCalendarAssignmentsTimelineStateArgs {
  selectedDateFilter: Date | null;
  viewMode: CalendarViewMode;
}

export function useCalendarAssignmentsTimelineState({
  selectedDateFilter,
  viewMode
}: UseCalendarAssignmentsTimelineStateArgs) {
  const baseDate = useMemo(
    () => (selectedDateFilter ? moment(Number(selectedDateFilter)) : moment()),
    [selectedDateFilter]
  );
  const startOfMonth = useMemo(
    () => baseDate.clone().startOf('month'),
    [baseDate]
  );
  const startOfNextMonth = useMemo(
    () => startOfMonth.clone().add(1, 'month'),
    [startOfMonth]
  );
  const monthDurationMs = startOfNextMonth.valueOf() - startOfMonth.valueOf();

  const [timelineScale, setTimelineScale] = useState<TimelineScale>(
    TimelineScale.MONTH
  );
  const [highlightedDateForView, setHighlightedDateForView] =
    useState<moment.Moment | null>(null);
  const [visibleTimeStart, setVisibleTimeStart] = useState<number>(
    startOfMonth.valueOf()
  );
  const [visibleTimeEnd, setVisibleTimeEnd] = useState<number>(
    startOfNextMonth.valueOf()
  );
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [timelineVersion, setTimelineVersion] = useState(0);

  const refreshTimeline = useCallback(() => {
    setTimelineVersion((version) => version + 1);
  }, []);

  const handleScaleChange = useCallback(
    (newScale: TimelineScale, date?: moment.Moment) => {
      setTimelineScale(newScale);
      if (newScale === TimelineScale.MONTH) {
        setHighlightedDateForView(null);
      } else if (date) {
        setHighlightedDateForView(date.clone());
      }
    },
    []
  );

  const handleSelectItem = useCallback(
    (itemId: number) => {
      if (viewMode === 'timesheet') return;
      setSelectedItemIds((selectedIds) =>
        selectedIds.includes(itemId) ? [] : [itemId]
      );
    },
    [viewMode]
  );

  const clearSelectedItems = useCallback(() => {
    setSelectedItemIds([]);
  }, []);

  const visibleTimeRange = useMemo(
    () => moment(visibleTimeStart),
    [visibleTimeStart]
  );

  const handleTimeChange = useCallback(
    (
      nextVisibleTimeStart: number,
      nextVisibleTimeEnd: number,
      updateScrollCanvas: (start: number, end: number) => void
    ) => {
      const minTime = startOfMonth.valueOf();
      const maxTime = startOfNextMonth.valueOf();
      const requestedDuration = nextVisibleTimeEnd - nextVisibleTimeStart;

      let clampedStart = nextVisibleTimeStart;
      let clampedEnd = nextVisibleTimeEnd;

      if (requestedDuration >= maxTime - minTime) {
        clampedStart = minTime;
        clampedEnd = maxTime;
      } else if (nextVisibleTimeStart < minTime) {
        clampedStart = minTime;
        clampedEnd = minTime + requestedDuration;
      } else if (nextVisibleTimeEnd > maxTime) {
        clampedStart = maxTime - requestedDuration;
        clampedEnd = maxTime;
      }

      updateScrollCanvas(clampedStart, clampedEnd);
      setVisibleTimeStart(clampedStart);
      setVisibleTimeEnd(clampedEnd);
    },
    [startOfMonth, startOfNextMonth]
  );

  useEffect(() => {
    let start: number;
    let end: number;

    if (timelineScale === TimelineScale.MONTH) {
      start = baseDate.clone().startOf('month').valueOf();
      end = baseDate.clone().endOf('month').valueOf();
    } else if (timelineScale === TimelineScale.WEEK) {
      const date = highlightedDateForView || baseDate;
      start = date.clone().startOf('isoWeek').valueOf();
      end = date.clone().endOf('isoWeek').valueOf();
    } else {
      const date = highlightedDateForView || baseDate;
      start = date.clone().startOf('day').valueOf();
      end = date.clone().endOf('day').valueOf();
    }

    setVisibleTimeStart(start);
    setVisibleTimeEnd(end);
    refreshTimeline();
  }, [timelineScale, baseDate, highlightedDateForView, refreshTimeline]);

  return {
    baseDate,
    startOfMonth,
    monthDurationMs,
    timelineScale,
    setTimelineScale,
    highlightedDateForView,
    visibleTimeRange,
    visibleTimeStart,
    visibleTimeEnd,
    selectedItemIds,
    selectedItemId: selectedItemIds[0]?.toString(),
    timelineVersion,
    setTimelineVersion,
    refreshTimeline,
    handleScaleChange,
    handleTimeChange,
    handleSelectItem,
    clearSelectedItems
  };
}
